import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { getOrdersByEmail } from '../../services/orderApi';
import './TrackOrder.css';

/**
 * Страница отслеживания заказа
 */
const TrackOrder = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPricing, setShowPricing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // Секции для навигации в Header
  const sections = [
    { id: 'hero', label: 'Home' },
    { id: 'testimonial', label: 'About Us' },
    { id: 'legal-help', label: 'Services' },
    { id: 'success-stories', label: 'Success Stories' },
    { id: 'features', label: 'Features' }
  ];

  // Тарифные планы
  const plans = [
    {
      id: 1,
      name: 'Basic',
      price: '299',
      color: 'purple',
      features: [
        'See basic financial management recommendations',
        'Get personalized assistance from our experts',
        'One-time debt analysis report'
      ]
    },
    {
      id: 2,
      name: 'Premium',
      price: '799',
      color: 'gold',
      features: [
        'See basic financial management recommendations',
        'Get personalized assistance from our experts',
        'Get detailed assistance from our lawyers',
        'Full debt negotiation service',
        'Priority customer support'
      ]
    }
  ];

  /**
   * Поиск заказов по email
   */
  const handleSearch = async (e) => {
    e.preventDefault();
    setError('');
    setOrders([]);
    setSelectedOrder(null);
    setShowPricing(false);
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);

    try {
      const foundOrders = await getOrdersByEmail(email.trim());
      
      if (foundOrders.length === 0) {
        setError('No orders found for this email address');
      } else {
        setOrders(foundOrders);
        // Автоматически выбираем первый заказ
        if (foundOrders.length > 0) {
          setSelectedOrder(foundOrders[0]);
        }
      }
    } catch (err) {
      console.error('Error searching orders:', err);
      setError('Failed to search orders. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Форматирование даты
   */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Получение времени ожидания
   */
  const getWaitingTime = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffMs = now - created;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    }
    return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
  };

  /**
   * Ускорить рассмотрение - показать тарифы
   */
  const handleAccelerate = () => {
    setShowPricing(true);
  };

  /**
   * Выбор тарифного плана
   */
  const handlePlanSelect = (planName) => {
    setSelectedPlan(planName);
  };

  /**
   * Продолжить с выбранным планом
   */
  const handleContinue = () => {
    if (selectedPlan && selectedOrder) {
      console.log('Selected plan:', selectedPlan, 'for order:', selectedOrder.id);
      // Здесь будет логика обновления заказа с тарифом
      navigate('/pricing-plans');
    }
  };

  return (
    <div className="track-order-page">
      <Header sections={sections} onJoinClick={openModal} />
      
      {/* Фон с зелеными кругами */}
      <div className="track-order-page__background">
        <div className="track-order-page__background--circle-one"></div>
        <div className="track-order-page__background--circle-two"></div>
      </div>

      {/* Основной контент */}
      <main className="track-order-page__main-container">
        <div className={`track-order-page__content-box ${showPricing ? 'track-order-page__content-box--pricing' : ''}`}>
          {!showPricing ? (
            <>
              <h1 className="track-order-page__title">Track Your Order</h1>
              <p className="track-order-page__subtitle">
                Enter your email address to track the status of your debt relief application
              </p>

              <form className="track-order-page__search-form" onSubmit={handleSearch}>
                <div className="track-order-page__search-form--input-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    required
                    disabled={isLoading}
                  />
                </div>

                <button type="submit" className="track-order-page__search-form--submit-button" disabled={isLoading}>
                  {isLoading ? 'Searching...' : 'Track Order'}
                </button>
              </form>

              {error && (
                <div className="track-order-page__error-message">
                  <p>{error}</p>
                </div>
              )}

              {selectedOrder && (
                <div className="track-order-page__order-details">
                  <div className="track-order-page__order-details--header">
                    <h2>Order Details</h2>
                    <span className={`track-order-page__order-details--status-badge track-order-page__order-details--status-${selectedOrder.status.toLowerCase()}`}>
                      {selectedOrder.status}
                    </span>
                  </div>

                  <div className="track-order-page__order-details--info-grid">
                    <div className="track-order-page__order-details--info-item">
                      <span className="track-order-page__order-details--info-label">Order ID:</span>
                      <span className="track-order-page__order-details--info-value">{selectedOrder.id}</span>
                    </div>

                    <div className="track-order-page__order-details--info-item">
                      <span className="track-order-page__order-details--info-label">Email:</span>
                      <span className="track-order-page__order-details--info-value">{selectedOrder.email}</span>
                    </div>

                    <div className="track-order-page__order-details--info-item">
                      <span className="track-order-page__order-details--info-label">Phone:</span>
                      <span className="track-order-page__order-details--info-value">{selectedOrder.phone || 'N/A'}</span>
                    </div>

                    <div className="track-order-page__order-details--info-item">
                      <span className="track-order-page__order-details--info-label">Country:</span>
                      <span className="track-order-page__order-details--info-value">{selectedOrder.country || 'N/A'}</span>
                    </div>

                    <div className="track-order-page__order-details--info-item">
                      <span className="track-order-page__order-details--info-label">State:</span>
                      <span className="track-order-page__order-details--info-value">{selectedOrder.state || 'N/A'}</span>
                    </div>

                    <div className="track-order-page__order-details--info-item">
                      <span className="track-order-page__order-details--info-label">Created:</span>
                      <span className="track-order-page__order-details--info-value">{formatDate(selectedOrder.createdAt)}</span>
                    </div>

                    <div className="track-order-page__order-details--info-item">
                      <span className="track-order-page__order-details--info-label">Waiting Time:</span>
                      <span className="track-order-page__order-details--info-value">{getWaitingTime(selectedOrder.createdAt)}</span>
                    </div>

                    {selectedOrder.amount && (
                      <div className="track-order-page__order-details--info-item">
                        <span className="track-order-page__order-details--info-label">Amount:</span>
                        <span className="track-order-page__order-details--info-value">${selectedOrder.finalAmount || selectedOrder.amount}</span>
                      </div>
                    )}
                  </div>

                  <div className="track-order-page__order-details--status-message">
                    <div className="track-order-page__order-details--status-icon">⏳</div>
                    <h3>Your order is awaiting review by our legal team</h3>
                    <p>
                      Our lawyers are currently reviewing your debt relief application. 
                      This process typically takes 2-3 business days.
                    </p>
                  </div>

                  <button className="track-order-page__order-details--accelerate-button" onClick={handleAccelerate}>
                    ⚡ Accelerate Review Process
                  </button>
                </div>
              )}

              {orders.length > 1 && (
                <div className="track-order-page__other-orders">
                  <h3>Your Other Orders</h3>
                  <div className="track-order-page__other-orders--list">
                    {orders.filter(o => o.id !== selectedOrder?.id).map((order) => (
                      <div 
                        key={order.id} 
                        className="track-order-page__other-orders--item"
                        onClick={() => setSelectedOrder(order)}
                      >
                        <span className="track-order-page__other-orders--item-id">{order.id}</span>
                        <span className={`track-order-page__other-orders--item-status track-order-page__other-orders--status-${order.status.toLowerCase()}`}>
                          {order.status}
                        </span>
                        <span className="track-order-page__other-orders--item-date">{formatDate(order.createdAt)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="track-order-page__support-info">
                <p>
                  Need help? Contact our support team at{' '}
                  <a href="mailto:support@debtagent.com">support@debtagent.com</a>
                </p>
              </div>
            </>
          ) : (
            <>
              {/* Pricing Plans Section */}
              <div className="track-order-page__pricing-section">
                <button className="track-order-page__pricing-section--back-button" onClick={() => setShowPricing(false)}>
                  ← Back to Order Details
                </button>
                <div className="track-order-page__pricing-section--icon">👨‍💼</div>
                <h1 className="track-order-page__pricing-section--title">
                  Accelerate Your Review Process
                </h1>
                <p className="track-order-page__pricing-section--subtitle">
                  Select a premium plan to get priority review from our legal team
                </p>
              </div>

              <div className="track-order-page__plans-grid">
                {plans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`track-order-page__plans-grid--plan-card track-order-page__plans-grid--plan-${plan.color} ${selectedPlan === plan.name ? 'track-order-page__plans-grid--plan-selected' : ''}`}
                    onClick={() => handlePlanSelect(plan.name)}
                  >
                    <div className="track-order-page__plans-grid--plan-header">
                      <h3 className="track-order-page__plans-grid--plan-name">{plan.name}</h3>
                      <p className="track-order-page__plans-grid--plan-description">
                        {plan.name === 'Basic' 
                          ? 'See basic financial management recommendations' 
                          : 'Priority review and full legal support'}
                      </p>
                    </div>

                    <div className="track-order-page__plans-grid--plan-features">
                      {plan.features.map((feature, index) => (
                        <div key={index} className="track-order-page__plans-grid--feature-item">
                          <Check size={16} className="track-order-page__plans-grid--feature-check" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="track-order-page__plans-grid--plan-price">
                      <span className="track-order-page__plans-grid--price-amount">{plan.price}</span>
                      <span className="track-order-page__plans-grid--price-currency">$</span>
                    </div>

                    {selectedPlan === plan.name && (
                      <div className="track-order-page__plans-grid--selected-badge">Selected</div>
                    )}
                  </div>
                ))}
              </div>

              <div className="track-order-page__pricing-actions">
                <button 
                  className="track-order-page__pricing-actions--continue-button"
                  onClick={handleContinue}
                  disabled={!selectedPlan}
                >
                  Continue with {selectedPlan || 'Selected Plan'}
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />

      {/* Modal Window */}
      {isModalOpen && (
        <div className="track-order-page__modal-overlay" onClick={closeModal}>
          <div className="track-order-page__modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="track-order-page__modal-content--title">Are you sure you want to continue?</h3>
            <div className="track-order-page__modal-content--buttons">
              <button className="track-order-page__modal-content--confirm-button" onClick={() => { closeModal(); navigate('/contact-form'); }}>
                Yes
              </button>
              <button className="track-order-page__modal-content--cancel-button" onClick={closeModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackOrder;
